import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  Bell, 
  MapPin, 
  Clock, 
  ThumbsUp, 
  ThumbsDown,
  ChefHat,
  Loader2,
  Calendar,
  Users,
  Sun,
  Moon,
  Euro,
  Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ParticipationRequestsSection({ requests, organisedRequests = [], token: propToken, onRequestHandled }) {
  const navigate = useNavigate();
  const [respondingTo, setRespondingTo] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState({});  // {eventId: [slot1, slot2]}
  
  // Use prop token or fallback to localStorage
  const getToken = () => propToken || localStorage.getItem("token");

  // Toggle slot selection
  const toggleSlot = (eventId, slotId) => {
    setSelectedSlots(prev => {
      const current = prev[eventId] || [];
      if (current.includes(slotId)) {
        return { ...prev, [eventId]: current.filter(s => s !== slotId) };
      } else {
        return { ...prev, [eventId]: [...current, slotId] };
      }
    });
  };

  // Check if slot is selected
  const isSlotSelected = (eventId, slotId) => {
    return (selectedSlots[eventId] || []).includes(slotId);
  };

  // Response for normal events (proposals)
  const respondToParticipation = async (eventId, interested) => {
    setRespondingTo(eventId);
    try {
      const response = await fetch(`${API_URL}/events/${eventId}/participation-response`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ interested })
      });
      
      if (response.ok) {
        toast.success("Réponse enregistrée");
        onRequestHandled();
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      console.error("Error responding:", error);
      toast.error("Erreur de connexion");
    } finally {
      setRespondingTo(null);
    }
  };

  // Response for organised events with slot selection
  const respondToOrganisedEventWithSlots = async (eventId, interested) => {
    const slots = selectedSlots[eventId] || [];
    
    if (interested && slots.length === 0) {
      toast.error("Veuillez sélectionner au moins un créneau");
      return;
    }
    
    setRespondingTo(`org-${eventId}`);
    try {
      const response = await fetch(`${API_URL}/organised-events/${eventId}/respond-slots`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          interested,
          selected_slots: interested ? slots : []
        })
      });
      
      if (response.ok) {
        toast.success(interested 
          ? `Intérêt enregistré pour ${slots.length} créneau(x)` 
          : "Réponse enregistrée"
        );
        setSelectedSlots(prev => ({ ...prev, [eventId]: [] }));
        onRequestHandled();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Erreur lors de l'enregistrement");
      }
    } catch (error) {
      console.error("Error responding to organised event:", error);
      toast.error("Erreur de connexion");
    } finally {
      setRespondingTo(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { 
        weekday: 'short', day: 'numeric', month: 'short' 
      });
    } catch {
      return dateStr;
    }
  };

  // Filter out declined requests from FT view
  const pendingRequests = requests?.filter(r => r.status !== "declined" && r.status !== "not_interested") || [];
  const pendingOrganisedRequests = organisedRequests?.filter(r => r.status !== "declined" && r.status !== "not_interested") || [];
  
  const totalRequests = pendingRequests.length + pendingOrganisedRequests.length;
  
  if (totalRequests === 0) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="participation-requests-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
          <Bell className="w-5 h-5 text-amber-600" />
          Demandes de participation ({totalRequests})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-amber-700">
          Vous avez reçu des demandes pour participer aux événements suivants :
        </p>
        
        {/* Organisation Events Requests */}
        {organisedRequests && organisedRequests.length > 0 && (
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-amber-800 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Événements organisés par le bureau
            </h5>
            {pendingOrganisedRequests.map((request) => (
              <div 
                key={request.id} 
                className="bg-white p-4 rounded-lg border border-amber-200"
                data-testid={`organised-request-${request.event_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-secondary">{request.event?.titre || "Événement"}</h4>
                      <Badge variant="outline" className="border-purple-300 text-purple-600 text-xs">
                        Organisation
                      </Badge>
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <p className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> {request.event?.lieu || "Lieu non précisé"}
                      </p>
                      {request.event?.dates?.length > 0 && (
                        <p className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> 
                          {request.event.dates.map(d => d.date).join(", ")}
                        </p>
                      )}
                      {request.event?.nombre_food_trucks_requis && (
                        <p className="flex items-center gap-2">
                          <Users className="w-4 h-4" /> 
                          {request.event.nombre_food_trucks_requis} food trucks recherchés
                        </p>
                      )}
                      {request.event?.specialites_recherchees?.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          <ChefHat className="w-4 h-4 text-orange-500" />
                          <span className="text-orange-600 text-xs">Spécialités :</span>
                          {request.event.specialites_recherchees.map((spec, i) => (
                            <Badge key={i} variant="outline" className="border-orange-300 text-orange-600 text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {request.event?.description && (
                      <p className="mt-2 text-sm text-gray-600 italic">
                        {request.event.description}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Slots Selection */}
                {request.available_slots && request.available_slots.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Sélectionnez les créneaux qui vous intéressent :
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {request.available_slots.map((slot) => (
                        <label
                          key={slot.slot_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSlotSelected(request.event_id, slot.slot_id)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                          }`}
                        >
                          <Checkbox
                            checked={isSlotSelected(request.event_id, slot.slot_id)}
                            onCheckedChange={() => toggleSlot(request.event_id, slot.slot_id)}
                            className="data-[state=checked]:bg-green-600"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="font-medium text-sm">{formatDate(slot.date)}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                              {slot.type === 'midi' ? (
                                <Sun className="w-3 h-3 text-amber-500" />
                              ) : (
                                <Moon className="w-3 h-3 text-indigo-500" />
                              )}
                              <span className="capitalize">{slot.type}</span>
                              <span className="text-gray-400">|</span>
                              <Clock className="w-3 h-3" />
                              <span>{slot.horaire_debut} - {slot.horaire_fin}</span>
                              {slot.couverts && (
                                <>
                                  <span className="text-gray-400">|</span>
                                  <Users className="w-3 h-3" />
                                  <span>{slot.couverts} cvts</span>
                                </>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    {(selectedSlots[request.event_id]?.length || 0) > 0 && (
                      <p className="text-sm text-green-600 mt-2">
                        ✓ {selectedSlots[request.event_id].length} créneau(x) sélectionné(s)
                        {request.event?.frais_par_service && (
                          <span className="text-gray-600"> - Total: {selectedSlots[request.event_id].length * request.event.frais_par_service}€</span>
                        )}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2 justify-end mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    onClick={() => navigate(`/planning-unifie?event=${request.event_id}&type=organised`)}
                    data-testid={`btn-view-details-org-${request.event_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Voir détails
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => respondToOrganisedEventWithSlots(request.event_id, false)}
                    disabled={respondingTo === `org-${request.event_id}`}
                    data-testid={`btn-not-interested-org-${request.event_id}`}
                  >
                    {respondingTo === `org-${request.event_id}` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ThumbsDown className="w-4 h-4 mr-1" />
                        Pas intéressé
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => respondToOrganisedEventWithSlots(request.event_id, true)}
                    disabled={respondingTo === `org-${request.event_id}` || (selectedSlots[request.event_id]?.length || 0) === 0}
                    data-testid={`btn-interested-org-${request.event_id}`}
                  >
                    {respondingTo === `org-${request.event_id}` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ThumbsUp className="w-4 h-4 mr-1" />
                        Intéressé ({selectedSlots[request.event_id]?.length || 0})
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Normal Event Requests (Proposals) */}
        {requests && requests.length > 0 && (
          <div className="space-y-3">
            {organisedRequests && organisedRequests.length > 0 && (
              <h5 className="text-sm font-medium text-amber-800 flex items-center gap-2 mt-4">
                <Calendar className="w-4 h-4" />
                Événements proposés par les membres
              </h5>
            )}
            {pendingRequests.map((request) => (
              <div 
                key={request.id} 
                className="bg-white p-4 rounded-lg border border-amber-200"
                data-testid={`participation-request-${request.event_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-secondary">{request.event?.titre || "Événement"}</h4>
                      {request.event?.proposeur_nom && (
                        <Badge variant="outline" className="border-blue-300 text-blue-600 text-xs">
                          Proposé par {request.event.proposeur_nom}
                        </Badge>
                      )}
                      {request.event?.responsable_nom && (
                        <Badge variant="outline" className="border-purple-300 text-purple-600 text-xs">
                          Resp: {request.event.responsable_nom}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <p className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> {request.event?.lieu || "Lieu non précisé"}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock className="w-4 h-4" /> {request.event?.date_debut 
                          ? new Date(request.event.date_debut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
                          : "Date non précisée"}
                      </p>
                      {request.event?.specialites_recherchees?.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          <ChefHat className="w-4 h-4 text-orange-500" />
                          <span className="text-orange-600 text-xs">Spécialités recherchées :</span>
                          {request.event.specialites_recherchees.map((spec, i) => (
                            <Badge key={i} variant="outline" className="border-orange-300 text-orange-600 text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {request.event?.information_complementaire && (
                      <p className="mt-2 text-sm text-gray-600 italic">
                        {request.event.information_complementaire}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      onClick={() => navigate(`/events/${request.event_id}`)}
                      data-testid={`btn-view-details-${request.event_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir détails
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => respondToParticipation(request.event_id, false)}
                      disabled={respondingTo === request.event_id}
                      data-testid={`btn-not-interested-${request.event_id}`}
                    >
                      {respondingTo === request.event_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          Pas intéressé
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => respondToParticipation(request.event_id, true)}
                      disabled={respondingTo === request.event_id}
                      data-testid={`btn-interested-${request.event_id}`}
                    >
                      {respondingTo === request.event_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          Intéressé
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
